import type { HabitMeasure } from './habitMeasure'

export type HabitCadence = 'daily' | 'everyOtherDay' | 'weekly' | 'monthly' | 'weekend' | 'weekday'
export type HabitTimeOfDay = 'morning' | 'afternoon' | 'evening'

// Default clock time for a suggested habit's time of day.
export const TIME_OF_DAY_CLOCK: Record<HabitTimeOfDay, string> = {
  morning: '08:00',
  afternoon: '13:00',
  evening: '19:00',
}

const BREATH_MINUTES: HabitMeasure = {
  direction: 'atLeast',
  target: 5,
  unit: 'min',
  quickAdds: [5],
  timer: true,
}

// Popular habit suggestions. Category drives the accent color + filter; cadence
// pre-fills the recurrence. Clients localize titles via `inbox.popularHabits.<key>`;
// the backend (habit-suggestion pushes) uses POPULAR_HABIT_TITLES below.
// Kept to the safe, universally-beneficial, low-stakes tier (no calorie/weight
// targets, nothing restrictive; quitting smoking, vaping or alcohol counts as beneficial).
export const POPULAR_HABITS: {
  key: string
  category: string
  cadence: HabitCadence
  timeOfDay: HabitTimeOfDay
  icon: string
  // Number habit to create instead of a check-off (unit localized by the clients).
  measure?: HabitMeasure
}[] = [
  { key: 'meditate', category: 'mind', cadence: 'daily', timeOfDay: 'morning', icon: 'leaf' },
  { key: 'gratitude3', category: 'mind', cadence: 'daily', timeOfDay: 'evening', icon: 'heart' },
  { key: 'journal', category: 'mind', cadence: 'daily', timeOfDay: 'evening', icon: 'pen-line' },
  { key: 'readPages', category: 'mind', cadence: 'daily', timeOfDay: 'evening', icon: 'book-open' },
  {
    key: 'phoneFreeHour',
    category: 'mind',
    cadence: 'daily',
    timeOfDay: 'morning',
    icon: 'phone-off',
  },
  { key: 'singleTask', category: 'mind', cadence: 'daily', timeOfDay: 'afternoon', icon: 'target' },
  { key: 'threeBreaths', category: 'mind', cadence: 'daily', timeOfDay: 'afternoon', icon: 'wind' },
  {
    key: 'boxBreathing',
    category: 'mind',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'wind',
    measure: BREATH_MINUTES,
  },
  {
    key: 'nadiShodhana',
    category: 'mind',
    cadence: 'daily',
    timeOfDay: 'morning',
    icon: 'wind',
    measure: BREATH_MINUTES,
  },
  { key: 'reflectWin', category: 'mind', cadence: 'daily', timeOfDay: 'evening', icon: 'trophy' },
  {
    key: 'socialWindow',
    category: 'mind',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'message-circle',
  },
  {
    key: 'stillness',
    category: 'mind',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'hourglass',
  },
  { key: 'waterWake', category: 'health', cadence: 'daily', timeOfDay: 'morning', icon: 'droplet' },
  { key: 'walk', category: 'health', cadence: 'daily', timeOfDay: 'afternoon', icon: 'footprints' },
  {
    key: 'stretch',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'morning',
    icon: 'person-standing',
  },
  { key: 'sleep', category: 'health', cadence: 'daily', timeOfDay: 'evening', icon: 'bed-double' },
  {
    key: 'breath478',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'moon',
    measure: BREATH_MINUTES,
  },
  {
    key: 'consistentBedtime',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'moon',
  },
  { key: 'eatVeg', category: 'health', cadence: 'daily', timeOfDay: 'afternoon', icon: 'carrot' },
  {
    key: 'takeStairs',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'mountain',
  },
  {
    key: 'moveHourly',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'timer',
  },
  { key: 'morningSun', category: 'health', cadence: 'daily', timeOfDay: 'morning', icon: 'sun' },
  { key: 'sunriseSunset', category: 'health', cadence: 'daily', timeOfDay: 'evening', icon: 'sun' },
  {
    key: 'grounding',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'morning',
    icon: 'footprints',
  },
  {
    key: 'strengthMobility',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'dumbbell',
  },
  {
    key: 'reachOut',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'phone',
  },
  {
    key: 'appreciation',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'gift',
  },
  {
    key: 'deviceFreeMeal',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'utensils',
  },
  {
    key: 'reallyListen',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'ear',
  },
  {
    key: 'kindAct',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'hand-heart',
  },
  {
    key: 'checkInFriend',
    category: 'relationships',
    cadence: 'weekly',
    timeOfDay: 'afternoon',
    icon: 'message-circle',
  },
  {
    key: 'shareProud',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'star',
  },
  {
    key: 'compliment',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'smile',
  },
  {
    key: 'qualityTime',
    category: 'relationships',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'users',
  },
  {
    key: 'reconnectOld',
    category: 'relationships',
    cadence: 'weekly',
    timeOfDay: 'afternoon',
    icon: 'phone',
  },
  {
    key: 'reviewPriority',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'morning',
    icon: 'list-checks',
  },
  {
    key: 'learn15',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'graduation-cap',
  },
  {
    key: 'importantFirst',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'morning',
    icon: 'target',
  },
  {
    key: 'realBreak',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'coffee',
  },
  {
    key: 'tidyDesk',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'sparkles',
  },
  {
    key: 'weeklyReview',
    category: 'career',
    cadence: 'weekly',
    timeOfDay: 'morning',
    icon: 'calendar',
  },
  {
    key: 'topThreeTomorrow',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'list-checks',
  },
  {
    key: 'networkReach',
    category: 'career',
    cadence: 'weekly',
    timeOfDay: 'afternoon',
    icon: 'users',
  },
  {
    key: 'reflectLearned',
    category: 'career',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'lightbulb',
  },
  { key: 'deepFocus', category: 'career', cadence: 'daily', timeOfDay: 'afternoon', icon: 'brain' },
  { key: 'logSpend', category: 'finance', cadence: 'daily', timeOfDay: 'evening', icon: 'receipt' },
  {
    key: 'budgetReview',
    category: 'finance',
    cadence: 'weekly',
    timeOfDay: 'evening',
    icon: 'wallet',
  },
  {
    key: 'noSpendDay',
    category: 'finance',
    cadence: 'weekly',
    timeOfDay: 'afternoon',
    icon: 'coins',
  },
  {
    key: 'saveSmall',
    category: 'finance',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'piggy-bank',
  },
  {
    key: 'financeGoal',
    category: 'finance',
    cadence: 'weekly',
    timeOfDay: 'afternoon',
    icon: 'trending-up',
  },
  {
    key: 'write10',
    category: 'creative',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'pencil',
  },
  {
    key: 'sketch',
    category: 'creative',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'palette',
  },
  {
    key: 'practiceSkill',
    category: 'creative',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'music',
  },
  {
    key: 'captureIdea',
    category: 'creative',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'lightbulb',
  },
  {
    key: 'makeSomething',
    category: 'creative',
    cadence: 'daily',
    timeOfDay: 'afternoon',
    icon: 'hammer',
  },
  // Quitting reads as a daily "free day" check-in, so the streak is the count of days.
  {
    key: 'smokeFree',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'cigarette-off',
  },
  {
    key: 'vapeFree',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'cigarette-off',
  },
  {
    key: 'alcoholFree',
    category: 'health',
    cadence: 'daily',
    timeOfDay: 'evening',
    icon: 'wine-off',
  },
]

export const POPULAR_HABIT_CATEGORIES = [
  'mind',
  'health',
  'relationships',
  'career',
  'finance',
  'creative',
]

// Mirrors the clients' inbox.popularHabits.<key> strings.
export const POPULAR_HABIT_TITLES: Record<string, Record<string, string>> = {
  en: {
    sunriseSunset: 'Watch the sunrise or sunset outside',
    grounding: 'Stand barefoot on grass or sand (grounding)',
    smokeFree: 'Have a smoke-free day',
    vapeFree: 'Have a vape-free day',
    alcoholFree: 'Have an alcohol-free day',
    meditate: 'Meditate for 10 minutes',
    gratitude3: "Write down three things you're grateful for",
    journal: 'Journal for a few minutes',
    readPages: 'Read a few pages of a book',
    phoneFreeHour: 'Spend the first hour phone-free',
    singleTask: 'Do one thing without multitasking',
    threeBreaths: 'Take three deep breaths before a stressful task',
    boxBreathing: 'Box breathing for 5 minutes',
    nadiShodhana: 'Alternate-nostril breathing (pranayama), 5 minutes',
    breath478: '4-7-8 breathing before bed',
    reflectWin: 'Reflect on one win from the day',
    socialWindow: 'Limit social media to a set window',
    stillness: 'Spend a few minutes in silence',
    waterWake: 'Drink a glass of water when you wake up',
    walk: 'Take a 20–30 minute walk',
    stretch: 'Stretch for 5 minutes',
    sleep: 'Get 7–9 hours of sleep',
    consistentBedtime: 'Go to bed at a consistent time',
    eatVeg: 'Eat a vegetable with lunch or dinner',
    takeStairs: 'Take the stairs when you can',
    moveHourly: 'Stand up and move every hour',
    morningSun: 'Get some sunlight in the morning',
    strengthMobility: 'Do a short strength or mobility session',
    reachOut: 'Message or call someone you care about',
    appreciation: 'Express appreciation to one person',
    deviceFreeMeal: 'Have one device-free meal',
    reallyListen: 'Really listen in a conversation',
    kindAct: 'Do one small kind thing for someone',
    checkInFriend: "Check in on a friend you haven't spoken to",
    shareProud: "Share something you're proud of",
    compliment: 'Give a genuine compliment',
    qualityTime: 'Spend quality time with family or partner',
    reconnectOld: 'Reconnect with one old friend',
    reviewPriority: 'Review your top priority for the day',
    learn15: 'Learn something new for 15 minutes',
    importantFirst: 'Do your most important task first',
    realBreak: 'Take a real break away from your desk',
    tidyDesk: 'Tidy your workspace at the end of the day',
    weeklyReview: 'Do a weekly review of goals and progress',
    topThreeTomorrow: "Write down tomorrow's top three tasks",
    networkReach: 'Reach out to one person in your field',
    reflectLearned: 'Reflect on what you learned today',
    deepFocus: 'Protect one block of deep-focus time',
    logSpend: 'Log what you spent today',
    budgetReview: 'Review your budget',
    noSpendDay: 'Have one no-spend day',
    saveSmall: 'Move a small amount to savings',
    financeGoal: "Check one financial goal's progress",
    write10: 'Write for 10 minutes',
    sketch: 'Sketch or doodle',
    practiceSkill: 'Practice an instrument or skill',
    captureIdea: 'Capture one idea in a notes app',
    makeSomething: 'Make something small with your hands',
  },
  es: {
    sunriseSunset: 'Mira el amanecer o el atardecer al aire libre',
    grounding: 'Camina descalzo sobre hierba o arena (grounding)',
    smokeFree: 'Un día sin fumar',
    vapeFree: 'Un día sin vapear',
    alcoholFree: 'Un día sin alcohol',
    meditate: 'Medita 10 minutos',
    gratitude3: 'Anota tres cosas que agradeces',
    journal: 'Escribe en tu diario unos minutos',
    readPages: 'Lee unas páginas de un libro',
    phoneFreeHour: 'Pasa la primera hora sin teléfono',
    singleTask: 'Haz una cosa sin multitarea',
    threeBreaths: 'Respira hondo tres veces antes de algo estresante',
    boxBreathing: 'Respiración cuadrada 5 minutos',
    nadiShodhana: 'Respiración alterna por las fosas nasales (pranayama), 5 minutos',
    breath478: 'Respiración 4-7-8 antes de dormir',
    reflectWin: 'Reflexiona sobre un logro del día',
    socialWindow: 'Limita las redes a un horario fijo',
    stillness: 'Pasa unos minutos en silencio',
    waterWake: 'Bebe un vaso de agua al despertar',
    walk: 'Camina 20–30 minutos',
    stretch: 'Estírate 5 minutos',
    sleep: 'Duerme 7–9 horas',
    consistentBedtime: 'Acuéstate a una hora fija',
    eatVeg: 'Come una verdura en la comida o cena',
    takeStairs: 'Usa las escaleras cuando puedas',
    moveHourly: 'Levántate y muévete cada hora',
    morningSun: 'Toma sol por la mañana',
    strengthMobility: 'Haz una sesión corta de fuerza o movilidad',
    reachOut: 'Escribe o llama a alguien que aprecias',
    appreciation: 'Expresa aprecio a una persona',
    deviceFreeMeal: 'Haz una comida sin dispositivos',
    reallyListen: 'Escucha de verdad en una conversación',
    kindAct: 'Haz algo amable por alguien',
    checkInFriend: 'Escríbele a un amigo con quien no hablas',
    shareProud: 'Comparte algo de lo que estés orgulloso',
    compliment: 'Haz un cumplido sincero',
    qualityTime: 'Pasa tiempo de calidad con tu familia o pareja',
    reconnectOld: 'Retoma el contacto con un viejo amigo',
    reviewPriority: 'Revisa tu prioridad del día',
    learn15: 'Aprende algo nuevo 15 minutos',
    importantFirst: 'Haz primero tu tarea más importante',
    realBreak: 'Tómate un descanso lejos del escritorio',
    tidyDesk: 'Ordena tu espacio al final del día',
    weeklyReview: 'Haz una revisión semanal de metas',
    topThreeTomorrow: 'Anota tus tres tareas de mañana',
    networkReach: 'Contacta a alguien de tu sector',
    reflectLearned: 'Reflexiona sobre lo que aprendiste hoy',
    deepFocus: 'Protege un bloque de concentración profunda',
    logSpend: 'Registra lo que gastaste hoy',
    budgetReview: 'Revisa tu presupuesto',
    noSpendDay: 'Ten un día sin gastos',
    saveSmall: 'Aparta un poco de dinero al ahorro',
    financeGoal: 'Revisa el progreso de una meta financiera',
    write10: 'Escribe 10 minutos',
    sketch: 'Dibuja o garabatea',
    practiceSkill: 'Practica un instrumento o habilidad',
    captureIdea: 'Anota una idea en una app de notas',
    makeSomething: 'Haz algo pequeño con las manos',
  },
  fr: {
    sunriseSunset: 'Regarder le lever ou le coucher du soleil dehors',
    grounding: 'Marcher pieds nus sur l’herbe ou le sable (grounding)',
    smokeFree: 'Une journée sans fumer',
    vapeFree: 'Une journée sans vapoter',
    alcoholFree: 'Une journée sans alcool',
    meditate: 'Méditer 10 minutes',
    gratitude3: 'Noter trois choses dont on est reconnaissant',
    journal: 'Écrire dans son journal quelques minutes',
    readPages: "Lire quelques pages d'un livre",
    phoneFreeHour: 'Passer la première heure sans téléphone',
    singleTask: 'Faire une chose sans multitâche',
    threeBreaths: 'Respirer profondément trois fois avant une tâche stressante',
    boxBreathing: 'Respiration carrée 5 minutes',
    nadiShodhana: 'Respiration alternée par les narines (pranayama), 5 minutes',
    breath478: 'Respiration 4-7-8 avant de dormir',
    reflectWin: 'Repenser à une réussite de la journée',
    socialWindow: 'Limiter les réseaux à un créneau défini',
    stillness: 'Passer quelques minutes en silence',
    waterWake: "Boire un verre d'eau au réveil",
    walk: 'Marcher 20–30 minutes',
    stretch: "S'étirer 5 minutes",
    sleep: 'Dormir 7–9 heures',
    consistentBedtime: 'Se coucher à heure régulière',
    eatVeg: 'Manger un légume au déjeuner ou dîner',
    takeStairs: "Prendre les escaliers quand c'est possible",
    moveHourly: 'Se lever et bouger chaque heure',
    morningSun: 'Prendre la lumière du matin',
    strengthMobility: 'Faire une courte séance de renfo ou mobilité',
    reachOut: 'Écrire ou appeler un proche',
    appreciation: "Exprimer sa reconnaissance à quelqu'un",
    deviceFreeMeal: 'Prendre un repas sans écran',
    reallyListen: 'Vraiment écouter dans une conversation',
    kindAct: "Faire une petite gentillesse à quelqu'un",
    checkInFriend: "Prendre des nouvelles d'un ami perdu de vue",
    shareProud: "Partager une fierté avec quelqu'un",
    compliment: 'Faire un compliment sincère',
    qualityTime: 'Passer du temps de qualité en famille ou en couple',
    reconnectOld: 'Renouer avec un vieil ami',
    reviewPriority: 'Revoir sa priorité du jour',
    learn15: 'Apprendre du neuf 15 minutes',
    importantFirst: "Faire d'abord sa tâche la plus importante",
    realBreak: 'Faire une vraie pause loin du bureau',
    tidyDesk: 'Ranger son espace en fin de journée',
    weeklyReview: 'Faire un bilan hebdo des objectifs',
    topThreeTomorrow: 'Noter ses trois tâches de demain',
    networkReach: 'Contacter une personne de son domaine',
    reflectLearned: "Repenser à ce qu'on a appris aujourd'hui",
    deepFocus: 'Protéger un créneau de concentration',
    logSpend: 'Noter ses dépenses du jour',
    budgetReview: 'Revoir son budget',
    noSpendDay: 'Passer une journée sans dépense',
    saveSmall: 'Mettre un peu de côté',
    financeGoal: "Suivre l'avancée d'un objectif financier",
    write10: 'Écrire 10 minutes',
    sketch: 'Croquer ou griffonner',
    practiceSkill: 'Pratiquer un instrument ou une compétence',
    captureIdea: 'Noter une idée dans une appli',
    makeSomething: 'Créer une petite chose de ses mains',
  },
  ja: {
    sunriseSunset: '外で朝日か夕日を眺める',
    grounding: '芝生や砂の上を裸足で歩く（グラウンディング）',
    smokeFree: 'タバコを吸わない日にする',
    vapeFree: '電子タバコを吸わない日にする',
    alcoholFree: 'お酒を飲まない日にする',
    meditate: '10分間瞑想する',
    gratitude3: '感謝できることを3つ書く',
    journal: '数分間日記を書く',
    readPages: '本を数ページ読む',
    phoneFreeHour: '最初の1時間はスマホなしで過ごす',
    singleTask: 'マルチタスクせず一つに集中する',
    threeBreaths: 'ストレスな作業の前に深呼吸を3回',
    boxBreathing: 'ボックス呼吸を5分',
    nadiShodhana: '片鼻呼吸（プラーナヤーマ）を5分',
    breath478: '寝る前に4-7-8呼吸',
    reflectWin: '今日の小さな成功を振り返る',
    socialWindow: 'SNSは決めた時間だけにする',
    stillness: '数分間静かに過ごす',
    waterWake: '起きたら水を1杯飲む',
    walk: '20〜30分歩く',
    stretch: '5分ストレッチする',
    sleep: '7〜9時間眠る',
    consistentBedtime: '毎日同じ時間に寝る',
    eatVeg: '昼か夜に野菜を食べる',
    takeStairs: 'できるときは階段を使う',
    moveHourly: '1時間ごとに立って動く',
    morningSun: '朝に日光を浴びる',
    strengthMobility: '短い筋トレやモビリティをする',
    reachOut: '大切な人に連絡する',
    appreciation: '誰か一人に感謝を伝える',
    deviceFreeMeal: '画面なしで食事する',
    reallyListen: '会話で本当に耳を傾ける',
    kindAct: '誰かに小さな親切をする',
    checkInFriend: '連絡していない友人に連絡する',
    shareProud: '誇れることを誰かに話す',
    compliment: '心からの褒め言葉を伝える',
    qualityTime: '家族やパートナーと過ごす',
    reconnectOld: '昔の友人と再会する',
    reviewPriority: '今日の最優先事項を確認する',
    learn15: '15分間新しいことを学ぶ',
    importantFirst: '一番大事な仕事を最初にやる',
    realBreak: 'デスクを離れて休憩する',
    tidyDesk: '一日の終わりに机を片づける',
    weeklyReview: '週に一度、目標を振り返る',
    topThreeTomorrow: '明日の重要タスク3つを書く',
    networkReach: '同業の誰かに連絡する',
    reflectLearned: '今日学んだことを振り返る',
    deepFocus: '集中できる時間を確保する',
    logSpend: '今日使ったお金を記録する',
    budgetReview: '予算を見直す',
    noSpendDay: '一日お金を使わない',
    saveSmall: '少額を貯金に回す',
    financeGoal: 'お金の目標の進み具合を確認',
    write10: '10分間書く',
    sketch: 'スケッチや落書きをする',
    practiceSkill: '楽器やスキルを練習する',
    captureIdea: 'アイデアをメモアプリに残す',
    makeSomething: '手を使って小さな物を作る',
  },
  pt: {
    sunriseSunset: 'Ver o nascer ou o pôr do sol ao ar livre',
    grounding: 'Ficar descalço na grama ou na areia (grounding)',
    smokeFree: 'Um dia sem fumar',
    vapeFree: 'Um dia sem vape',
    alcoholFree: 'Um dia sem álcool',
    meditate: 'Medite 10 minutos',
    gratitude3: 'Anote três coisas pelas quais é grato',
    journal: 'Escreva no diário por alguns minutos',
    readPages: 'Leia algumas páginas de um livro',
    phoneFreeHour: 'Passe a primeira hora sem celular',
    singleTask: 'Faça uma coisa sem multitarefa',
    threeBreaths: 'Respire fundo três vezes antes de algo estressante',
    boxBreathing: 'Respiração quadrada por 5 minutos',
    nadiShodhana: 'Respiração alternada pelas narinas (pranayama), 5 minutos',
    breath478: 'Respiração 4-7-8 antes de dormir',
    reflectWin: 'Reflita sobre uma vitória do dia',
    socialWindow: 'Limite as redes a um horário fixo',
    stillness: 'Passe alguns minutos em silêncio',
    waterWake: 'Beba um copo de água ao acordar',
    walk: 'Caminhe 20–30 minutos',
    stretch: 'Alongue-se 5 minutos',
    sleep: 'Durma 7–9 horas',
    consistentBedtime: 'Deite-se em horário regular',
    eatVeg: 'Coma um vegetal no almoço ou jantar',
    takeStairs: 'Use as escadas quando puder',
    moveHourly: 'Levante e mova-se a cada hora',
    morningSun: 'Pegue sol de manhã',
    strengthMobility: 'Faça uma sessão curta de força ou mobilidade',
    reachOut: 'Escreva ou ligue para alguém querido',
    appreciation: 'Demonstre apreço a uma pessoa',
    deviceFreeMeal: 'Faça uma refeição sem telas',
    reallyListen: 'Escute de verdade numa conversa',
    kindAct: 'Faça uma pequena gentileza a alguém',
    checkInFriend: 'Fale com um amigo distante',
    shareProud: 'Compartilhe algo de que se orgulha',
    compliment: 'Faça um elogio sincero',
    qualityTime: 'Passe tempo de qualidade com a família ou parceiro',
    reconnectOld: 'Reconecte-se com um amigo antigo',
    reviewPriority: 'Revise sua prioridade do dia',
    learn15: 'Aprenda algo novo por 15 minutos',
    importantFirst: 'Faça primeiro sua tarefa mais importante',
    realBreak: 'Faça uma pausa real longe da mesa',
    tidyDesk: 'Organize seu espaço no fim do dia',
    weeklyReview: 'Faça uma revisão semanal de metas',
    topThreeTomorrow: 'Anote as três tarefas de amanhã',
    networkReach: 'Contate alguém da sua área',
    reflectLearned: 'Reflita sobre o que aprendeu hoje',
    deepFocus: 'Proteja um bloco de foco profundo',
    logSpend: 'Registre o que gastou hoje',
    budgetReview: 'Revise seu orçamento',
    noSpendDay: 'Tenha um dia sem gastos',
    saveSmall: 'Guarde um pouco na poupança',
    financeGoal: 'Veja o progresso de uma meta financeira',
    write10: 'Escreva 10 minutos',
    sketch: 'Rabisque ou desenhe',
    practiceSkill: 'Pratique um instrumento ou habilidade',
    captureIdea: 'Anote uma ideia num app de notas',
    makeSomething: 'Faça algo pequeno com as mãos',
  },
  zh: {
    sunriseSunset: '到户外看日出或日落',
    grounding: '赤脚站在草地或沙滩上（接地）',
    smokeFree: '今天不吸烟',
    vapeFree: '今天不吸电子烟',
    alcoholFree: '今天不喝酒',
    meditate: '冥想10分钟',
    gratitude3: '写下三件感恩的事',
    journal: '写几分钟日记',
    readPages: '读书几页',
    phoneFreeHour: '第一个小时不碰手机',
    singleTask: '专注做一件事不分心',
    threeBreaths: '做压力大的事前深呼吸三次',
    boxBreathing: '箱式呼吸5分钟',
    nadiShodhana: '交替鼻孔呼吸（调息法）5分钟',
    breath478: '睡前做4-7-8呼吸',
    reflectWin: '回顾今天的一个小成就',
    socialWindow: '把刷社交媒体限定在固定时段',
    stillness: '静静待几分钟',
    waterWake: '起床后喝一杯水',
    walk: '散步20–30分钟',
    stretch: '拉伸5分钟',
    sleep: '睡够7–9小时',
    consistentBedtime: '固定时间上床睡觉',
    eatVeg: '午餐或晚餐吃一份蔬菜',
    takeStairs: '能走楼梯就走楼梯',
    moveHourly: '每小时起身活动一下',
    morningSun: '早晨晒点太阳',
    strengthMobility: '做一次简短的力量或灵活性训练',
    reachOut: '给在乎的人发消息或打电话',
    appreciation: '向一个人表达欣赏',
    deviceFreeMeal: '一顿饭不看电子设备',
    reallyListen: '在对话中认真倾听',
    kindAct: '为某人做一件小善事',
    checkInFriend: '联系一位久未联系的朋友',
    shareProud: '分享一件让你自豪的事',
    compliment: '真诚地赞美一句',
    qualityTime: '与家人或伴侣共度好时光',
    reconnectOld: '重新联系一位老朋友',
    reviewPriority: '确认今天最重要的事',
    learn15: '学新东西15分钟',
    importantFirst: '先做最重要的任务',
    realBreak: '离开工位好好休息一下',
    tidyDesk: '一天结束时整理工位',
    weeklyReview: '每周回顾目标与进展',
    topThreeTomorrow: '写下明天最重要的三件事',
    networkReach: '联系一位同行',
    reflectLearned: '回顾今天学到了什么',
    deepFocus: '留出一段深度专注时间',
    logSpend: '记录今天的花费',
    budgetReview: '检查你的预算',
    noSpendDay: '有一天不花钱',
    saveSmall: '存一小笔钱',
    financeGoal: '查看一个理财目标的进展',
    write10: '写作10分钟',
    sketch: '涂鸦或素描',
    practiceSkill: '练习一门乐器或技能',
    captureIdea: '把一个想法记进笔记应用',
    makeSomething: '动手做点小东西',
  },
}
